import { isNullish } from '@toolbox/utils';

import type { Nullable } from '@toolbox/utils';
import type { UseFormGetValues, Validate, Message, FieldValues } from 'react-hook-form';
import type { ValidateResult } from 'react-hook-form/dist/types/validator';

export type ValidatorProps<FD extends FieldValues> = {
  getValues: UseFormGetValues<FD>;
  market?: string;
};

export type ValidateFactory<V> = <FD extends FieldValues>(props: ValidatorProps<FD>) => Validate<V>;

export type ValidateFn<V, C extends Record<never, never> | undefined = undefined, R = boolean> = <
  FD extends FieldValues,
>(
  props: { getValues: UseFormGetValues<FD>; value: V; market?: string },
  config: C,
) => R | Promise<R>;

export type GetError<V, R, C extends Record<never, never> | undefined = undefined> = (
  props: { value: V; result?: R; error?: Error },
  config: C,
) => Message | Message[];

export type ValidatorConfig<V, R, C extends Record<never, never> | undefined = undefined> = {
  getError?: GetError<V, R, C>;
} & (C extends undefined ? Record<never, never> : { config: C });

export function createValidator<V, C extends undefined = undefined, R = boolean>(
  validate: ValidateFn<V, C, R>,
): (getError: GetError<V, R, C>) => (config?: ValidatorConfig<V, R, C>) => ValidateFactory<V>;
export function createValidator<V, C extends Record<never, never>, R = boolean>(
  validate: ValidateFn<V, C, R>,
): (getError: GetError<V, R, C>) => (config: C | ValidatorConfig<V, R, C>) => ValidateFactory<V>;
export function createValidator<V, C extends Record<never, never> | undefined, R = boolean>(
  validate: ValidateFn<V, C, R>,
) {
  return (getError: GetError<V, R, C>) =>
    (config?: C): ValidateFactory<V> =>
    (props) =>
    async (value) => {
      const propsWithValue = { ...props, value };
      try {
        const result = await validate(propsWithValue, config as C);

        return (result as unknown) === true || getError({ ...propsWithValue, result }, config as C);
      } catch (error) {
        return getError({ ...propsWithValue, error: error as Error | undefined }, config as C);
      }
    };
}

export function passEmpty<V, C extends Record<never, never> | undefined = undefined, R = boolean>(
  validate: ValidateFn<NonNullable<V>, C, R>,
) {
  return async <FD extends FieldValues>(
    props: {
      value: Nullable<V>;
      getValues: UseFormGetValues<FD>;
    },
    config: C,
  ): Promise<boolean | R> => {
    return (
      isEmpty(props.value) ||
      (await validate(props as { value: NonNullable<V>; getValues: UseFormGetValues<FD> }, config))
    );
  };
}

export function extendErrorMessage<V>(
  validator: ValidateFactory<V>,
  getError: <FD extends FieldValues>(
    props: ValidatorProps<FD>,
    value: V,
    result: ValidateResult,
  ) => ValidateResult,
): ValidateFactory<V> {
  return (props) => (value: V) => {
    const result = validator(props)(value);

    if (result instanceof Promise) {
      return result.then((result) => result === true || getError(props, value, result));
    }

    return result === true || getError(props, value, result);
  };
}

export function isEmpty(value: unknown): boolean {
  return (typeof value === 'string' && value.trim() === '') || isNullish(value);
}
